import React from 'react';
import { Box, useTheme, CircularProgress } from '@mui/material';
import { SxProps } from '@mui/system';
import { font } from '../../utils/fontHelper';

export type ButtonSemantic = 'brand' | 'secondary' | 'tertiary' | 'accent' | 'like' | 'success' | 'warning' | 'error';
export type ButtonSize = 'small' | 'medium';
export type ButtonVariant = 'contained' | 'outlined' | 'text';

interface CustomButtonProps {
  semantic?: ButtonSemantic;
  size?: ButtonSize;
  variant?: ButtonVariant;
  icon?: React.ReactNode;
  label?: string;
  iconOnly?: boolean;
  loading?: boolean;
  disabled?: boolean;
  onClick?: (event?: React.MouseEvent<HTMLElement>) => void;
  sx?: SxProps;
  children?: React.ReactNode;
  'aria-label'?: string;
}

const CustomButton: React.FC<CustomButtonProps> = ({
  semantic = 'accent',
  size = 'medium',
  variant = 'contained',
  icon,
  label,
  iconOnly = false,
  loading = false,
  disabled = false,
  onClick,
  sx = {},
  children,
  'aria-label': ariaLabel,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Size configurations
  const sizeConfig = {
    small: {
      padding: iconOnly ? '12px' : '12px 12px',
      fontSize: '14px',
      iconSize: '16px',
      minWidth: iconOnly ? '40px' : '64px',
      height: '40px',
    },
    medium: {
      padding: iconOnly ? '16px' : '16px 16px',
      fontSize: '14px',
      iconSize: '20px',
      minWidth: iconOnly ? '52px' : '88px',
      height: '52px',
    },
  };

  // Color configurations based on updated Figma design
  const getSemanticColors = () => {
    const colors = {
      accent: {
        light: { bg: '#3634A3', text: 'rgba(255, 255, 255, 0.9)', border: '#3634A3' },
        dark: { bg: '#7D7AFF', text: 'rgba(0, 0, 0, 0.9)', border: '#7D7AFF' }
      },
      secondary: {
        light: { bg: 'transparent', text: '#3634A3', border: '#3634A3' },
        dark: { bg: 'transparent', text: '#7D7AFF', border: '#7D7AFF' }
      },
      tertiary: {
        light: { bg: 'rgba(0, 0, 0, 0.08)', text: '#3634A3', border: 'transparent' },
        dark: { bg: 'rgba(255, 255, 255, 0.15)', text: 'rgba(255, 255, 255, 0.9)', border: 'transparent' }
      },
      brand: {
        light: { bg: '#D30F45', text: 'rgba(255, 255, 255, 0.9)', border: '#8944AB' },
        dark: { bg: '#FF6482', text: 'rgb(43, 13, 22)', border: '#DA8FFF' }
      },
      like: {
        light: { bg: '#8944AB', text: 'rgba(245, 233, 249, 0.9)', border: '#8944AB' },
        dark: { bg: '#DA8FFF', text: 'rgba(32, 2, 42, 1)', border: '#DA8FFF' }
      },
      success: {
        light: { bg: '#248A3D', text: 'rgba(255, 255, 255, 0.9)', border: '#248A3D' },
        dark: { bg: '#30D158', text: 'rgba(0, 0, 0, 0.9)', border: '#30D158' }
      },
      warning: {
        light: { bg: '#C93400', text: 'rgba(255, 255, 255, 0.9)', border: '#C93400' },
        dark: { bg: '#FFB340', text: 'rgba(0, 0, 0, 0.9)', border: '#FFB340' }
      },
      error: {
        light: { bg: '#D70015', text: 'rgba(255, 255, 255, 0.9)', border: '#D70015' },
        dark: { bg: '#FF6961', text: 'rgba(0, 0, 0, 0.9)', border: '#FF6961' }
      },
    };

    return colors[semantic][isDark ? 'dark' : 'light'];
  };

  // Get variant-specific styles
  const getVariantStyles = () => {
    const semanticColors = getSemanticColors();
    const currentSize = sizeConfig[size];

    const baseStyles = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: iconOnly ? 0 : '8px',
      padding: currentSize.padding,
      minWidth: currentSize.minWidth,
      height: currentSize.height,
      borderRadius: '12px',
      border: 'none',
      cursor: disabled || loading ? 'not-allowed' : 'pointer',
      fontFamily: font(),
      fontSize: currentSize.fontSize,
      fontWeight: 600,
      letterSpacing: '0.2px',
      textAlign: 'center' as const,
      textTransform: 'none' as const,
      transition: 'all 0.2s ease',
      opacity: disabled ? 0.5 : 1,
      '&:hover': !disabled && !loading ? {
        opacity: 0.8,
        transform: 'translateY(-1px)',
      } : {},
      '&:active': !disabled && !loading ? {
        transform: 'translateY(0px)',
      } : {},
    };

    switch (variant) {
      case 'contained':
        return {
          ...baseStyles,
          backgroundColor: semanticColors.bg,
          color: semanticColors.text,
        };
      
      case 'outlined':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: semanticColors.text,
          border: `1px solid ${semanticColors.border}`,
        };
      
      case 'text':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
          color: semanticColors.text,
        };
      
      default:
        return baseStyles;
    }
  };

  // Handle click
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled && !loading && onClick) {
      onClick(event);
    }
  };

  const buttonStyles = getVariantStyles();
  const currentSize = sizeConfig[size];

  return (
    <Box
      component="button"
      type="button"
      aria-label={ariaLabel}
      onClick={handleClick}
      sx={{
        ...buttonStyles,
        ...sx,
      }}
    >
      {/* Loading State */}
      {loading && (
        <CircularProgress
          size={currentSize.iconSize}
          sx={{
            color: 'currentColor',
            marginRight: !iconOnly && (label || children) ? '6px' : 0,
          }}
        />
      )}

      {/* Icon */}
      {!loading && icon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: currentSize.iconSize,
            height: currentSize.iconSize,
            '& svg': {
              width: currentSize.iconSize,
              height: currentSize.iconSize,
            },
          }}
        >
          {icon}
        </Box>
      )}

      {/* Label */}
      {!iconOnly && (label || children) && (
        <Box
          component="span"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {children || label}
        </Box>
      )}
    </Box>
  );
};

export default CustomButton;